/**
 * My Financial Assistant for a locked member (TMMAPP-0142 M1; Operator, 2026-10-16): Help & feedback's
 * help, announcements and tickets, mirroring tmm-api's lock filter, which lets a locked member reach
 * the six ticket routes (file, list, view, reply, close, and download a ticket's file) and, since
 * TMMAPI-0186 U1 item 7 (TMMAPP-0153 item 18), the help library's reads and the announcements.
 *
 * - The locked banner shows with its renew path.
 * - The panel stands on its own: no `ensure`, no session, no chat and no New conversation (each
 *   would answer `403 SUBSCRIPTION_EXPIRED`); asking the support assistant stays locked.
 * - New ticket offers a support case only, and nothing can be attached (uploads are locked).
 * - A ticket link (`?ticket={number}`) opens that ticket; the parameter is then dropped.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Box, Paper } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { parseTicketNumber } from '@/api/tickets';
import { LockedBanner } from '@/session/LockedScreen';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';
import { SupportPanel, type SupportRequest } from './support/SupportPanel';

export function LockedAssistant() {
  const { t } = useTranslation();
  const title = usePageTitle({ title: t('nav.assistant') });
  const headingId = useId();
  const [searchParams, setSearchParams] = useSearchParams();
  const ticketParam = parseTicketNumber(searchParams.get('ticket'));
  const [request, setRequest] = useState<SupportRequest | null>(null);
  const nonce = useRef(0);

  useEffect(() => {
    if (ticketParam === null) return;
    queueMicrotask(() => {
      nonce.current += 1;
      setRequest({ kind: 'ticket', number: ticketParam, nonce: nonce.current });
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete('ticket');
          return next;
        },
        { replace: true },
      );
    });
  }, [ticketParam, setSearchParams]);

  return (
    <>
      <PageHeader
        title={title}
        subtitle={
          <Box component="span" sx={{ display: 'block', maxWidth: 620 }}>
            {t('support.askLocked')}
          </Box>
        }
      />
      <LockedBanner />
      <Paper
        component="section"
        aria-labelledby={headingId}
        sx={{ maxWidth: 720, mt: 2 }}
        data-pane="tickets"
      >
        <SupportPanel
          headingId={headingId}
          supportSessionId={null}
          request={request}
          ticketsVersion={0}
          locked
        />
      </Paper>
    </>
  );
}
